// IE Hub - Terms & Conditions Service
// Handles terms acceptance API operations

import { httpPost } from '../http';
import { termsEndpoints, customerEndpoints } from '../endpoints';
import { femaCapture } from '../../fema/capture';
import { getDecodedCustomerType } from '@/shared/prearrears/ctToken';
import { ActionType } from '../types';
import type {
  RequestConfig,
  ServiceResponse,
} from '../types';

// Terms acceptance request payload
export interface AcceptTermsRequest {
  debtor1FirstName?: string;
  debtor1LastName?: string;
  displayName: string;
  emailAddress: string;
  userId: string | number;
  termsAndConditionsAccepted: string; // Expected to be "true" or similar
  /**
   * Legacy customer type (decoded from qsParam base64), e.g. `barclays_pre-arrears`.
   * Backend uses this to persist pre-arrears status.
   */
  customerType?: string;
}

// Terms acceptance response
export interface AcceptTermsResponse {
  message: string;
  variant: 'success' | 'error' | 'info';
  myData?: any;
}

/**
 * Terms Service Class
 * Handles terms and conditions acceptance with proper error handling and monitoring
 */
export class TermsService {
  /**
   * Accept terms and conditions
   */
  static async acceptTerms(
    termsData: AcceptTermsRequest, 
    config?: RequestConfig
  ): Promise<ServiceResponse<AcceptTermsResponse>> {
    try {
      const response = await httpPost<AcceptTermsResponse>(
        termsEndpoints.acceptTerms,
        termsData,
        {
          timeout: config?.timeout || 10000,
          headers: {
            'Content-Type': 'application/json',
            ...config?.headers
          }
        }
      );

      // Log the event to ie-event-log after successful T&C acceptance
      try {
        await httpPost(
          customerEndpoints.logButtonAction(),
          {
            actionType: ActionType.BUTTON_CLICK,
            actionName: 'Accept Terms and Conditions',
            pageName: 'AcceptTerms',
            subSection: 'Legal',
          },
          { skipErrorCapture: true }
        );
      } catch (logError) {
        // Silent catch for logging failures to prevent blocking the main flow
        console.warn('TermsService - Failed to log T&C acceptance event:', logError);
      }

      return {
        success: true,
        data: response.data,
        message: response.data.message || 'Terms accepted successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error: any) {
      if (!config?.skipErrorCapture) {
        femaCapture('terms_acceptance_error', error, {
          category: 'legal',
          severity: 'error',
          context: {
            userId: termsData.userId,
            endpoint: termsEndpoints.acceptTerms
          },
          skipSanitization: false,
          user: {
            id: termsData.userId.toString()
          }
        });
      }
      throw error;
    }
  }

  /**
   * Utility function to prepare terms data from user object
   */
  static prepareTermsData(user: {
    userId: string | number;
    displayName?: string;
    emailAddress?: string;
  }): Omit<AcceptTermsRequest, 'termsAndConditionsAccepted'> {
    const displayName = user.displayName?.trim() ?? '';
    const nameParts = displayName ? displayName.split(/\s+/).filter(Boolean) : [];

    const baseData: Omit<AcceptTermsRequest, 'termsAndConditionsAccepted'> = {
      displayName,
      emailAddress: user.emailAddress ?? '',
      userId: user.userId ?? '',
    };

    if (nameParts.length >= 2) {
      const [firstName, ...lastNameParts] = nameParts;
      if (firstName) {
        baseData.debtor1FirstName = firstName;
      }
      const lastName = lastNameParts.join(' ');
      if (lastName) {
        baseData.debtor1LastName = lastName;
      }
    }

    return baseData;
  }

  /**
   * Complete terms acceptance with user data
   */
  static async acceptTermsForUser(
    user: {
      userId: string | number;
      displayName?: string;
      emailAddress?: string;
    },
    config?: RequestConfig
  ): Promise<ServiceResponse<AcceptTermsResponse>> {
    const baseData = this.prepareTermsData(user);
    
    const customerType = getDecodedCustomerType();
    const fullTermsData: AcceptTermsRequest = {
      ...baseData,
      // Backend expects uppercase "Y" per Swagger screenshot
      termsAndConditionsAccepted: 'Y',
      ...(customerType ? { customerType } : {}),
    };

    return this.acceptTerms(fullTermsData, config);
  }
}

export default TermsService;
